import { useCountUp } from '../hooks/useCountUp';
import { formatPoints, getCurrentMonthLabel } from '../utils/redeemUtils';

type RedeemHeroProps = {
  points: number;
  tickets: number;
  onScrollToRent: () => void;
  onScrollToGiftCards: () => void;
  onGoEarn: () => void;
  onEVoucher: () => void;
};

const PARTICLES = [
  { cls: 'gold', top: '18%', left: '12%', size: 5, delay: '0s' },
  { cls: 'blue', top: '32%', left: '38%', size: 4, delay: '1.2s' },
  { cls: 'gold', top: '62%', left: '8%', size: 6, delay: '2.4s' },
  { cls: 'blue', top: '78%', left: '28%', size: 4, delay: '.8s' },
  { cls: 'gold', top: '46%', left: '46%', size: 5, delay: '3.1s' },
  { cls: 'blue', top: '22%', left: '62%', size: 4, delay: '1.7s' },
  { cls: 'gold', top: '84%', left: '54%', size: 5, delay: '2.2s' },
  { cls: 'blue', top: '14%', left: '88%', size: 4, delay: '.4s' },
  { cls: 'gold', top: '88%', left: '80%', size: 6, delay: '3.6s' },
] as const;

export const RedeemHero = ({
  points,
  tickets,
  onScrollToRent,
  onScrollToGiftCards,
  onGoEarn,
  onEVoucher,
}: RedeemHeroProps) => {
  const animatedPoints = useCountUp(points, 1400);
  const monthLabel = getCurrentMonthLabel();

  return (
    <section className='rd-hero'>
      <div className='rd-hero-glow-blue' aria-hidden='true' />
      <div className='rd-hero-glow-gold' aria-hidden='true' />

      <div className='rd-hero-rings' aria-hidden='true'>
        <div className='rd-ring r-lg' />
        <div className='rd-ring r-md' />
        <div className='rd-ring r-sm' />
      </div>

      {PARTICLES.map((particle) => (
        <div
          key={`${particle.top}-${particle.left}`}
          className={`rd-particle ${particle.cls}`}
          style={{
            top: particle.top,
            left: particle.left,
            width: particle.size,
            height: particle.size,
            animationDelay: particle.delay,
          }}
          aria-hidden='true'
        />
      ))}

      <div className='rd-hero-grid'>
        <div>
          <div className='rd-status'>
            <span className='rd-status-tag live'>
              <span className='rd-live-dot' />
              Live
            </span>
            <span className='rd-status-tag muted'>{monthLabel}</span>
          </div>

          <div className='rd-eyebrow'>
            <span className='rd-eyebrow-rule' />
            <span className='rd-eyebrow-lbl'>Redeem & save</span>
          </div>

          <h1 className='rd-title'>
            Redeem your <span className='rd-gold'>points.</span>
          </h1>
          <p className='rd-sub'>
            Use your points for direct rent savings or gift cards from 115+ brands.
          </p>

          <div className='rd-hero-ctas'>
            <button type='button' className='rd-btn-primary' onClick={onScrollToRent}>
              Redeem points
            </button>
            <button type='button' className='rd-btn-ghost' onClick={onScrollToGiftCards}>
              View gift cards
            </button>
            <button type='button' className='rd-btn-ghost' onClick={onEVoucher}>
              E-Voucher
            </button>
          </div>
        </div>

        <div className='rd-hwallet-wrap'>
          <div className='rd-hwallet-badge'>
            <span className='rd-hwallet-badge-dot' />
            <span className='rd-hwallet-badge-txt'>
              <b>{formatPoints(tickets)}</b> tickets this month
            </span>
          </div>
          <div className='rd-hwallet'>
            <div className='rd-hwallet-content'>
              <div className='rd-hwallet-lbl'>Wallet balance</div>
              <div className='rd-hwallet-num'>{formatPoints(animatedPoints)}</div>
              <div className='rd-hwallet-unit'>pts</div>
              <div className='rd-hwallet-divider' />
              <button type='button' className='rd-hwallet-link' onClick={onGoEarn}>
                + Earn more
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
